// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, LinearProgress, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Pending from "@mui/icons-material/Pending";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Error from "@mui/icons-material/Error";
import { generatePath, useLocation, useNavigate, useParams } from "react-router";

import FileUploadArea from "./FileUploadArea";
import VirtualizedFileList from "./VirtualizedFileList";
import { createStyleSheet } from "../utils/styles";
import {
  FileStatus,
  PHOTOSET_PURPOSE,
  PROJECT_FLOW_QUERY_KEYS,
  PROJECT_FLOW_STEP_VALUES,
  type FileInfo,
  type FileUploadAreaRef,
  FileUploadApiStatus,
  type TestFileUploadProps,
} from "../types/projects";
import {
  CREATE_PROJECT_WITHOUT_TEST_PATH,
  UPDATE_PROJECT_WITHOUT_TEST_PATH,
  UPLOAD_TEST_ITEM_HEIGHT,
  UPLOAD_TEST_ITEM_MAX_HEIGHT,
} from "../utils/constants";
import { FileInput } from "./FileInput";
import {
  useCreatePhotosetMutation,
  useLazyGetPhotoUrlForUploadQuery,
  useLazyListPhotosetsQuery,
  useLazyGetRdtQuery,
  useUploadRdtExamplePhotoMutation,
} from "../features/data/projectApiSlice";
import { theme } from "../../theme";

const BATCH_SIZE = 5;

const TestFileUpload = (props: TestFileUploadProps) => {
  const { isFieldEvaluation, isThumbnailRequired = true, onContinue, onCancel } = props;
  const { t } = useTranslation("TestFileUploader");
  const instructions = t(isFieldEvaluation ? "fieldEvaluationInstruction" : "instructionsList", {
    returnObjects: true,
  }) as string[];
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [uploadRdtExamplePhoto] = useUploadRdtExamplePhotoMutation();
  const [getRdt] = useLazyGetRdtQuery();
  const [getPhotoUrlForUpload] = useLazyGetPhotoUrlForUploadQuery();
  const [listPhotosets, { data: listPhotosetsData, isFetching, originalArgs }] =
    useLazyListPhotosetsQuery();
  const [createPhotoset] = useCreatePhotosetMutation();
  const uploadRef = useRef<FileUploadAreaRef>(null);

  const [files, setFiles] = useState<FileInfo[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<FileStatus | "all">("all");
  const [photosetUid, setPhotosetUid] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null | string>(null);
  const [thumbnailError, setThumbnailError] = useState("");

  const uploadFiles = useCallback(async () => {
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      setFiles(prev =>
        prev.map(file =>
          batch.some(
            batchFile =>
              batchFile.hash === file.hash &&
              file.status !== "exists" &&
              file.status !== "completed" &&
              file.status !== "invalid"
          )
            ? { ...file, status: "checking" }
            : file
        )
      );
      for (const fileInfo of batch) {
        if (!photosetUid || fileInfo.status === "exists" || fileInfo.status === "invalid") continue;
        try {
          const response = await getPhotoUrlForUpload({
            hash: fileInfo.hash,
            photosetUid,
          }).unwrap();
          if (response.status === FileUploadApiStatus.UPLOADED) {
            setFiles(prev =>
              prev.map(f =>
                f.hash === fileInfo.hash ? { ...f, status: "exists", progress: 100 } : f
              )
            );
            continue;
          }

          if (response.status === FileUploadApiStatus.NEW && response.upload_url) {
            fileInfo.uploadURL = response.upload_url;
            setFiles(prev =>
              prev.map(f =>
                f.hash === fileInfo.hash ? { ...f, uploadURL: response.upload_url } : f
              )
            );
          }

          const filesToUpload = batch.filter(f => f.status !== "exists" && f.status !== "invalid");

          if (filesToUpload.length > 0) {
            filesToUpload.map(async fileInfo => {
              setFiles(prev =>
                prev.map(f => (f.hash === fileInfo.hash ? { ...f, status: "uploading" } : f))
              );

              if (fileInfo.uploadURL) {
                uploadToSignedUrl(fileInfo.uploadURL, fileInfo.file as File)
                  .then(response => {
                    if (response.ok) {
                      setFiles(prev =>
                        prev.map(f =>
                          f.hash === fileInfo.hash
                            ? { ...f, status: "completed", progress: 100, file: null }
                            : f
                        )
                      );
                    }
                  })
                  .catch(error => {
                    console.error("Error uploading file:", fileInfo.filename, error);
                    setFiles(prev =>
                      prev.map(f =>
                        f.hash === fileInfo.hash
                          ? {
                              ...f,
                              status: "error",
                              error:
                                typeof error === "string" ? error : error?.message || String(error),
                            }
                          : f
                      )
                    );
                  });
              }
            });
          }
        } catch (error) {
          console.error(`Error checking status for ${fileInfo?.filename}:`, error);
        }
      }
    }
  }, [files, getPhotoUrlForUpload, photosetUid]);

  const uploadToSignedUrl = async (uploadUrl: string, file: File) => {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });
    if (!response.ok) {
      setThumbnailError(response.statusText);
    }
    return response;
  };

  const removeFile = useCallback(
    (fileDetails: FileInfo) => {
      setFiles(prev =>
        prev.filter(file => {
          if (file.hash) {
            return file.hash !== fileDetails.hash;
          } else {
            return !(file.filename === fileDetails.filename && file.size === fileDetails.size);
          }
        })
      );
    },
    [setFiles]
  );

  const clearAll = useCallback(() => {
    setFiles([]);
    uploadRef.current?.clearFiles();
  }, []);

  const calculateListHeight = useMemo(() => {
    const calculatedHeight = Math.min(
      files.length * UPLOAD_TEST_ITEM_HEIGHT,
      UPLOAD_TEST_ITEM_MAX_HEIGHT
    );
    return Math.max(calculatedHeight, 200);
  }, [files.length]);
  const filterFilesByStatus = (files: FileInfo[], status: FileStatus | "all"): FileInfo[] => {
    if (status === "all") return files;
    return files.filter(file => file.status === status);
  };
  const filteredFiles = filterFilesByStatus(files, selectedStatus);
  const validFiles = files.filter(file => file.status !== "invalid");

  const getUploadButtonLabel = () => {
    const fileLabel = validFiles.length > 1 ? t("files") : t("file");
    return `${t("Upload")} ${validFiles.length} ${fileLabel}`;
  };
  const getStatusStats = () => {
    const stats = {
      pending: 0,
      checking: 0,
      uploading: 0,
      completed: 0,
      exists: 0,
      error: 0,
      invalid: 0,
    };

    files.forEach(file => {
      stats[file.status]++;
    });

    return stats;
  };
  const stats = getStatusStats();
  const totalCount = Object.entries(stats)
    .filter(([key]) => ["completed", "exists", "error"].includes(key))
    .reduce((sum, [, value]) => sum + value, 0);
  const onUploadRDTExamplePhoto = useCallback(
    async (file: File | null) => {
      const { testUid } = params;
      if (!file || !testUid) return;

      try {
        const { upload_url } = await uploadRdtExamplePhoto({ rdt_uid: testUid }).unwrap();

        // Upload the file to the signed URL
        await uploadToSignedUrl(upload_url, file);
      } catch (err) {
        console.error("Error uploading file:", err);
      }
    },
    [params, uploadRdtExamplePhoto]
  );
  const handleContinue = useCallback(() => {
    if (onContinue) {
      onContinue();
    } else {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.AI_MODEL_SETUP);
      const { orgUid = "", projectUid = "" } = params;
      const newPath = generatePath(
        projectUid ? UPDATE_PROJECT_WITHOUT_TEST_PATH : CREATE_PROJECT_WITHOUT_TEST_PATH,
        { orgUid, projectUid }
      );
      const newFullPath = `${newPath}?${searchParams.toString()}`;
      navigate(newFullPath, { state: { newTestUid: params.testUid } });
    }
  }, [location.search, params, navigate, onContinue]);

  useEffect(() => {
    const testUid = params.testUid;
    const projectUid = params.projectUid;
    if (isFieldEvaluation && projectUid) {
      listPhotosets({ projectUid });
    } else if (!isFieldEvaluation && testUid) {
      listPhotosets({ rdtUid: testUid });
    }
  }, [params, listPhotosets, isFieldEvaluation]);
  useEffect(() => {
    const testUid = params.testUid;
    const projectUid = params.projectUid;
    const isNewResponse = isFieldEvaluation
      ? originalArgs?.projectUid === projectUid
      : originalArgs?.rdtUid === testUid;
    if (listPhotosetsData && isNewResponse && !isFetching) {
      const { photosets = [] } = listPhotosetsData;
      const { key, value, purpose } = isFieldEvaluation
        ? { key: "project_uid", value: projectUid, purpose: PHOTOSET_PURPOSE.MONITORING }
        : { key: "rdt_uid", value: testUid, purpose: PHOTOSET_PURPOSE.DEVELOPMENT };

      const photoset = photosets.find(
        photoset => photoset[key as keyof typeof photoset] === value && photoset.purpose === purpose
      );
      if (photoset?.photoset_uid) {
        setPhotosetUid(photoset?.photoset_uid);
      } else if (projectUid || testUid) {
        const payload = isFieldEvaluation
          ? {
              projectUid: projectUid ?? null,
              rdtUid: null,
              purpose: PHOTOSET_PURPOSE.MONITORING,
            }
          : {
              projectUid: null,
              rdtUid: testUid ?? null,
              purpose: PHOTOSET_PURPOSE.DEVELOPMENT,
            };
        createPhotoset(payload)
          .unwrap()
          .then(response => {
            if (response?.photoset_uid) {
              setPhotosetUid(response.photoset_uid);
            }
          });
      }
    }
  }, [params, listPhotosetsData, createPhotoset, isFieldEvaluation, isFetching, originalArgs]);

  useEffect(() => {
    const { testUid } = params;
    if (!testUid) return;
    getRdt({ rdtUid: testUid }).then(response => {
      if (response?.data?.example_image_url) {
        setThumbnail(response.data.example_image_url);
      }
    });
  }, [params, getRdt]);

  return (
    <Box>
      {isThumbnailRequired && (
        <Box sx={styles.testImageUploadSection}>
          <FileInput
            value={thumbnail}
            onFileInputChange={file => {
              setThumbnail(file);
              onUploadRDTExamplePhoto(file);
            }}
          />
          {thumbnailError && (
            <Typography variant={"body2"} color="error">
              {thumbnailError}
            </Typography>
          )}
        </Box>
      )}
      <FileUploadArea ref={uploadRef} setFiles={setFiles} setProcessing={setProcessing} />
      <Box sx={styles.instructionsContainer}>
        <Typography sx={styles.instructionTitle}>{t("instructionsTitle")}</Typography>
        <Box component="ul" sx={styles.instructionList}>
          {instructions.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </Box>
      </Box>

      <Box sx={styles.infoBox}>
        <HelpOutlineIcon sx={styles.infoIcon} data-testid="HelpOutlineIcon" />
        <Typography sx={styles.infoText}>{t("infoText")}</Typography>
      </Box>
      <Box>
        <Typography sx={styles.instructionTitle}>
          <strong>{t("noteLabel")}</strong> {t("noteDescription")}
        </Typography>
      </Box>

      {files.length > 0 && (
        <>
          <Box sx={styles.headerBox}>
            <Typography variant="h6">{t("uploadSummary")}</Typography>
            <Button onClick={clearAll} color="error" variant="outlined" size="small">
              {t("clearAll")}
            </Button>
          </Box>
          <Box sx={styles.chipBox}>
            <Chip
              label={`Total: ${files.length}`}
              variant={selectedStatus === "all" ? "filled" : "outlined"}
              onClick={() => setSelectedStatus("all")}
            />
            <Chip
              label={`Pending: ${stats.pending}`}
              color="default"
              icon={<Pending />}
              variant={selectedStatus === FileStatus.Pending ? "filled" : "outlined"}
              onClick={() => setSelectedStatus(FileStatus.Pending)}
            />
            <Chip
              label={`Uploading: ${stats.uploading}`}
              color="warning"
              variant={selectedStatus === FileStatus.Uploading ? "filled" : "outlined"}
              onClick={() => setSelectedStatus(FileStatus.Uploading)}
            />
            <Chip
              label={`Completed: ${stats.completed}`}
              color="success"
              icon={<CheckCircle />}
              variant={selectedStatus === FileStatus.Completed ? "filled" : "outlined"}
              onClick={() => setSelectedStatus(FileStatus.Completed)}
            />
            <Chip
              label={`Exists: ${stats.exists}`}
              color="info"
              variant={selectedStatus === FileStatus.Exists ? "filled" : "outlined"}
              onClick={() => setSelectedStatus(FileStatus.Exists)}
            />
            <Chip
              label={`Error: ${stats.error}`}
              color="error"
              icon={<Error />}
              variant={selectedStatus === FileStatus.Error ? "filled" : "outlined"}
              onClick={() => setSelectedStatus(FileStatus.Error)}
            />
            <Chip
              label={`Invalid: ${stats.invalid}`}
              color="error"
              icon={<Error />}
              variant={selectedStatus === FileStatus.Invalid ? "filled" : "outlined"}
              onClick={() => setSelectedStatus(FileStatus.Invalid)}
            />
          </Box>

          {processing && (
            <Box sx={styles.progressBar}>
              <LinearProgress />
            </Box>
          )}
          <VirtualizedFileList
            files={filteredFiles}
            onRemoveFile={removeFile}
            height={calculateListHeight}
          />
          {validFiles.length > 0 && (
            <Box sx={styles.uploadButtonContainer}>
            {!isFieldEvaluation && (
              <Button variant="outlined" onClick={onCancel} sx={styles.cancelBtn}>
                {t("common:cancel")}
              </Button>
            )}
              {totalCount === files.length ? (
                <Button variant="contained" onClick={handleContinue}>
                  {t("continueText")}
                </Button>
              ) : (
                <Button variant="contained" onClick={uploadFiles} data-testid="upload-btn">
                  {getUploadButtonLabel()}
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TestFileUpload;

const styles = createStyleSheet({
  instructionsContainer: {
    marginBottom: "20px",
    color: theme.palette.common.black,
  },
  instructionTitle: {
    fontWeight: 400,
    marginBottom: "10px",
    color: theme.palette.text.secondary,
  },
  instructionList: {
    pl: 3,
    mt: 1,
    "& li": {
      marginBottom: 1,
      lineHeight: 1.6,
      fontSize: 16,
    },
  },
  infoBox: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.grey[100],
    padding: "16px 15px",
    borderRadius: "6px",
    marginBottom: "30px",
  },
  infoIcon: {
    color: theme.palette.primary.main,
    marginRight: 1,
  },
  infoText: {
    color: theme.palette.grey[800],
    fontSize: 16,
  },
  uploadButtonContainer: {
    textAlign: "center",
    marginTop: 3,
  },
  headerBox: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  chipBox: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    marginBottom: "30px",
  },
  progressBar: {
    mt: 2,
    mb: 2,
  },
  testImageUploadSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  cancelBtn: { mr: 2 },
});
